'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import IconSpinner from './icon-spinner';
import { X } from 'lucide-react';
import { cn } from './lib/utils';
import { useFlatUI } from './flat-ui';

interface DialogProps extends DialogPrimitive.DialogProps {
  open?: boolean;
  onClose?: () => void;
  loading?: boolean;
}

const Dialog: React.FC<DialogProps> = ({
  children,
  open,
  onClose,
  ...props
}) => {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onClose} {...props}>
      {children}
    </DialogPrimitive.Root>
  );
};

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  loading?: boolean;
  closeIcon?: boolean;
  fullWidth?: boolean;
  overflow?: boolean;
  scroll?: boolean;
  slide?: 'default' | 'l' | 'r' | 't' | 'b';
  title?: string;
  /** Render `title` visibly at the top in the standard dialog title style, not only for screen readers. */
  showTitle?: boolean;
  /** Skip the default title so a child DialogTitle is the one aria-labelledby points at. */
  omitTitle?: boolean;
  description?: string;
  /** Skip the default description so a child DialogDescription is the one aria-describedby points at. */
  omitDescription?: boolean;
  fullScreen?: boolean;
  overLay?: boolean;
  /**
   * Pin the close X to the viewport. Defaults to full-screen dialogs, whose
   * content scrolls. Pass false when a full-screen dialog is inset (a bottom
   * sheet) so the X stays in that sheet's corner.
   */
  pinClose?: boolean;
  /**
   * Called from the close X in addition to Radix dismiss. Use this when the
   * dialog is controlled and must not gain an overlay or Escape dismiss.
   * preventDefault stops that dismiss, for example while a save is in flight.
   */
  onCloseClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Focus the close X when the dialog opens. The X is the last control in the
   * dialog, so without this the first field takes focus.
   */
  autoFocusClose?: boolean;
  /**
   * Phone: full-width sheet anchored to the bottom. From `sm` up, a centered dialog.
   */
  mobileBottomSheet?: boolean;
  /** Sit above another open dialog. Use for a confirm opened from a modal. */
  elevated?: boolean;
}

const dialogTitleClassName = 'text-xl font-semibold leading-none tracking-tight mb-2';

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      fullScreen = false,
      overLay = true,
      closeIcon = false,
      pinClose,
      onCloseClick,
      autoFocusClose = false,
      mobileBottomSheet = false,
      elevated = false,
      loading = false,
      fullWidth = false,
      overflow = false,
      scroll = false,
      slide = 'default',
      title = 'Dialog',
      showTitle = false,
      omitTitle = false,
      description = 'Dialog content',
      onOpenAutoFocus,
      omitDescription = false,
      ...props
    },
    ref
  ) => {
    const flat = useFlatUI();
    const pinCloseToViewport = pinClose ?? fullScreen;
    // A shadcn dialog opened from inside a MUI dialog has to clear MUI's dialog layer, or it
    // is an invisible modal: still focus-trapped and still blocking, just not painted.

    return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(
          'fixed inset-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          elevated ? 'z-[10050]' : 'z-[3000]'
        )}
        style={{
          animationDuration: '400ms',
          backgroundColor: overLay ? 'rgba(0, 0, 0, 0.6)' : 'transparent',
        }}
      />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed border bg-background shadow-lg',
          elevated ? 'z-[10051]' : 'z-[9999]',
          fullScreen
            ? cn(
                'inset-0 w-screen h-screen sm:rounded-none p-4 sm:p-6 touch-manipulation pointer-events-auto overflow-y-auto overscroll-none',
                slide === 'b' && 'animate-slide-fullscreen-from-bottom'
              )
            : [
                // Single-column grid: default `minmax(auto, auto)` lets a long unbreakable child
                // (PDF filename, wide table) widen the track past max-w-*, then overflow-hidden
                // clips the footer. `minmax(0,1fr)` + min-w-0 let the card stay within max-width.
                'p-6 grid min-w-0 grid-cols-[minmax(0,1fr)] gap-4 sm:rounded-lg',
                mobileBottomSheet &&
                  'inset-x-0 bottom-0 top-auto w-full max-w-none translate-x-0 translate-y-0 rounded-b-none rounded-t-xl max-sm:max-h-[92vh] max-sm:overflow-y-auto sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-full sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2 sm:overflow-visible sm:rounded-lg',
                !mobileBottomSheet &&
                  slide === 'default' &&
                  'left-[50%] top-[50%] -translate-x-[50%] -translate-y-[50%] animate-fade-in',
                !mobileBottomSheet &&
                  slide === 'l' &&
                  'left-[50%] top-[50%] -translate-x-[50%] -translate-y-[50%] animate-slide-from-left',
                !mobileBottomSheet &&
                  slide === 'r' &&
                  'right-[50%] top-[50%] -translate-x-[-50%] -translate-y-[50%] animate-slide-from-right',
                !mobileBottomSheet &&
                  slide === 't' &&
                  'left-[50%] top-[50%] -translate-x-[50%] -translate-y-[50%] animate-slide-from-top',
                !mobileBottomSheet &&
                  slide === 'b' &&
                  'left-[50%] top-[50%] -translate-x-[50%] -translate-y-[50%] animate-slide-from-bottom',
                !mobileBottomSheet && (fullWidth ? 'w-full max-w-2xl' : 'w-full max-w-lg'),
                !mobileBottomSheet && (overflow ? '' : 'overflow-hidden'),
                !mobileBottomSheet && (scroll ? 'overflow-y-auto max-h-[85vh]' : ''),
              ],
          className,
          flat && 'shadow-none border-border'
        )}
        style={{ animationDuration: '200ms' }}
        onOpenAutoFocus={(event) => {
          onOpenAutoFocus?.(event);
          if (event.defaultPrevented || !autoFocusClose) return;
          event.preventDefault();
          const content = event.currentTarget as HTMLElement;
          const closeButton = Array.from(content.children).find(
            (node): node is HTMLButtonElement => node instanceof HTMLButtonElement
          );
          closeButton?.focus();
        }}
        {...props}
      >
        {omitTitle ? null : (
          <DialogPrimitive.Title
            className={showTitle ? cn(dialogTitleClassName, closeIcon && 'pr-10') : 'sr-only'}
          >
            {title}
          </DialogPrimitive.Title>
        )}
        {omitDescription ? null : (
          <DialogPrimitive.Description className="sr-only">
            {description}
          </DialogPrimitive.Description>
        )}
        <IconSpinner loading={loading} src="/img/chat.png">
          {children}
        </IconSpinner>
        {closeIcon ? (
          <DialogPrimitive.Close asChild>
            <button
              type="button"
              onClick={onCloseClick}
              className={cn(
                // A full-screen dialog scrolls its own content. Pin the X to the
                // viewport corner so it stays put. A centered dialog keeps the X
                // in the card's top-right, matching Submit New Dealership.
                pinCloseToViewport ? 'fixed' : 'absolute',
                'right-4 top-4 z-[10000] rounded-sm opacity-70 transition-opacity hover:opacity-100 disabled:pointer-events-none bg-white text-black p-2'
              )}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
    );
  }
);
DialogContent.displayName = 'DialogContent';

const DialogContentText = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-muted-foreground mb-4', className)}
    {...props}
  />
));
DialogContentText.displayName = 'DialogContentText';

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(dialogTitleClassName, className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogActions = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-row-reverse gap-2 mt-6', className)}
    {...props}
  />
);

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex justify-end gap-2 mt-6', className)} {...props} />
);

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground mb-4', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
};
